import { useSuspenseQueries } from '@tanstack/react-query';

import { getBarberByIdOrRouteQuery } from '@/api/booking/getBarberByIdOrRouteQuery';
import { getProfessionalsQuery } from '@/api/booking/getProfessionalsQuery';
import type { Professional } from '@/types/booking';

import type { UseSuspenseProfessionalQueryParams } from './types';

/**
 * Finds the professional in the shop's professionals list, which Choose a
 * professional has already cached, so normal navigation does not suspend.
 * Requests the professional alone only when the list does not have them.
 * Returns `undefined` for "any professional" without a request.
 */
export const useSuspenseProfessionalQuery = ({
  shopId,
  barberIdOrRoute,
}: UseSuspenseProfessionalQueryParams): Professional | undefined => {
  const listQueries: ReturnType<typeof getProfessionalsQuery>[] =
    barberIdOrRoute ? [getProfessionalsQuery(shopId)] : [];
  const professionals = useSuspenseQueries({ queries: listQueries }).at(0);
  const listed = professionals?.data.find(
    ({ barber }) =>
      barber.id === barberIdOrRoute || barber.route === barberIdOrRoute,
  );

  const singleQueries: ReturnType<typeof getBarberByIdOrRouteQuery>[] =
    barberIdOrRoute && !listed
      ? [getBarberByIdOrRouteQuery(shopId, barberIdOrRoute)]
      : [];
  const single = useSuspenseQueries({ queries: singleQueries }).at(0);

  return listed ?? single?.data;
};
